/**
 * PhaseTransition
 *
 * Full-screen ritual moment between phases. Fades in, holds, fades out.
 * Gives the session pacing and weight — signals something is about to happen.
 * Holds for 2.5s, fades for 0.5s, then calls onDone.
 *
 * The phase name and its descriptor come from the message catalogue. They used
 * to be English literals in this file, so a Polish session showed English on
 * this screen six times over (#63).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PHASE_KEYS } from '../../types/session'
import type { Phase } from '../../types/session'

interface PhaseTransitionProps {
  phase: Phase
  onDone: () => void
}

export default function PhaseTransition({ phase, onDone }: PhaseTransitionProps) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    // Hold for 2500ms, then fade out over 500ms, then call onDone
    const fadeTimer = setTimeout(() => setVisible(false), 2500)
    const doneTimer = setTimeout(onDone, 3000)
    return () => { clearTimeout(fadeTimer); clearTimeout(doneTimer) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{
        background: '#1C1128',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.5s ease',
        pointerEvents: 'none',
      }}
    >
      <p
        className="text-xs font-medium uppercase tracking-widest mb-3"
        style={{ color: 'rgba(155,114,192,0.7)' }}
      >
        {t(`session.phaseDescriptors.${PHASE_KEYS[phase]}`)}
      </p>
      <p
        className="font-display text-4xl"
        style={{ color: 'white', fontWeight: 300, fontStyle: 'italic', letterSpacing: '-0.01em' }}
      >
        {t(`session.phases.${PHASE_KEYS[phase]}`)}
      </p>

      {/* Subtle spiral dot */}
      <div
        className="mt-8 w-1.5 h-1.5 rounded-full"
        style={{ background: '#9B72C0', opacity: 0.6 }}
      />
    </div>
  )
}
